import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Portal,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import TuneIcon from '@mui/icons-material/Tune';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import CustomTableLayout, { CUSTOM_TABLE_LAYOUT_CONFIG } from '../components/tables/custom/CustomTableLayout';
import { TableCopyButton, TableEmptyState, TableHeader, TableLoadingState, TablePagination, TableRecordCount, TableSearch, TableSurface, TableToolbar, TABLE_HEADER_CELL_SX } from '../components/tables/shared/TablePrimitives';

const OPERATORS_BY_CATEGORY = {
  string: [
    { value: 'contains', label: 'contains' },
    { value: 'equals', label: 'equals' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  number: [
    { value: 'equals', label: '=' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '>=' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '<=' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  boolean: [
    { value: 'is_true', label: 'is True' },
    { value: 'is_false', label: 'is False' },
  ],
  date: [
    { value: 'equals', label: 'on date' },
    { value: 'before', label: 'before date' },
    { value: 'after', label: 'after date' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
};

/** Purpose: Perform get category for field type for the custom data module. Why: Callers need a consistent result from this shared operation. */
const getCategoryForFieldType = (type = 'string') => {
  if (type === 'number') return 'number';
  if (type === 'boolean') return 'boolean';
  if (type === 'date') return 'date';
  return 'string';
};

/** Purpose: Perform evaluate rule for the custom data module. Why: Callers need a consistent result from this shared operation. */
const evaluateRule = (rawVal, operator, filterVal, category) => {
  const isValEmpty = rawVal === null || rawVal === undefined || rawVal === '';
  if (operator === 'is_empty') return isValEmpty;
  if (operator === 'is_not_empty') return !isValEmpty;

  if (operator === 'is_true') return rawVal === true || rawVal === 'true';
  if (operator === 'is_false') return rawVal === false || rawVal === 'false';

  if (isValEmpty) return false;

  if (category === 'number') {
    const num = Number(rawVal);
    const target = Number(filterVal);
    if (Number.isNaN(num) || Number.isNaN(target)) return false;
    if (operator === 'equals') return num === target;
    if (operator === 'gt') return num > target;
    if (operator === 'gte') return num >= target;
    if (operator === 'lt') return num < target;
    if (operator === 'lte') return num <= target;
    return true;
  }

  if (category === 'date') {
    const dVal = new Date(rawVal).getTime();
    const dTarget = new Date(filterVal).getTime();
    if (Number.isNaN(dVal) || Number.isNaN(dTarget)) return false;
    if (operator === 'equals') {
      try {
        return new Date(rawVal).toISOString().slice(0, 10) === new Date(filterVal).toISOString().slice(0, 10);
      } catch {
        return false;
      }
    }
    if (operator === 'before') return dVal < dTarget;
    if (operator === 'after') return dVal > dTarget;
    return true;
  }

  const strVal = String(rawVal).toLowerCase();
  const strTarget = String(filterVal || '').toLowerCase();
  if (operator === 'contains') return strVal.includes(strTarget);
  if (operator === 'equals') return strVal === strTarget;
  if (operator === 'starts_with') return strVal.startsWith(strTarget);
  if (operator === 'not_contains') return !strVal.includes(strTarget);
  return true;
};

/** Purpose: Perform get field value for the custom data module. Why: Callers need a consistent result from this shared operation. */
const getFieldValue = (record, key) => {
  if (key === 'recordId') return record.recordId;
  if (key === 'updatedAt') return record.updatedAt || record.createdAt;
  if (key === 'createdAt') return record.createdAt;
  return record.data?.[key];
};

/** Purpose: Perform compare values for the custom data module. Why: Callers need a consistent result from this shared operation. */
const compareValues = (a, b, key, direction, fieldType) => {
  const valA = getFieldValue(a, key);
  const valB = getFieldValue(b, key);

  const aEmpty = valA === null || valA === undefined || valA === '';
  const bEmpty = valB === null || valB === undefined || valB === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  const category = getCategoryForFieldType(fieldType);
  if (category === 'number') {
    const numA = Number(valA);
    const numB = Number(valB);
    if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
      return direction === 'asc' ? numA - numB : numB - numA;
    }
  }

  if (category === 'boolean') {
    const bA = valA === true || valA === 'true';
    const bB = valB === true || valB === 'true';
    return direction === 'asc' ? (bA === bB ? 0 : bA ? 1 : -1) : (bA === bB ? 0 : bA ? -1 : 1);
  }

  if (category === 'date' || key === 'updatedAt' || key === 'createdAt') {
    const timeA = new Date(valA).getTime() || 0;
    const timeB = new Date(valB).getTime() || 0;
    return direction === 'asc' ? timeA - timeB : timeB - timeA;
  }

  const strA = typeof valA === 'object' ? JSON.stringify(valA) : String(valA);
  const strB = typeof valB === 'object' ? JSON.stringify(valB) : String(valB);
  return direction === 'asc'
    ? strA.localeCompare(strB, undefined, { numeric: true, sensitivity: 'base' })
    : strB.localeCompare(strA, undefined, { numeric: true, sensitivity: 'base' });
};

const FIELD_TYPES = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'json', label: 'JSON' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'textarea', label: 'Long text' },
];

let fieldRowSequence = 0;

/** Purpose: Handle create field row id in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
const createFieldRowId = () => {
  fieldRowSequence += 1;
  return `field-row-${Date.now()}-${fieldRowSequence}`;
};

/** Purpose: Build empty field within the custom data module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
const emptyField = () => ({
  _rowId: createFieldRowId(),
  key: '',
  label: '',
  type: 'string',
  required: false,
  unique: false,
  searchable: false,
  defaultValue: '',
});

/** Purpose: Perform normalize key for the custom data module. Why: Callers need a consistent result from this shared operation. */
const normalizeKey = (value = '') => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

/** Purpose: Perform format field type for the custom data module. Why: Callers need a consistent result from this shared operation. */
const formatFieldType = (type = 'string') => (
  FIELD_TYPES.find((item) => item.value === type)?.label || type
);

/** Purpose: Resolve short id for the custom data module. Why: The surrounding operation uses this value to make its decision. */
const shortId = (value) => {
  const text = String(value || '');
  return text.length > 20 ? `${text.slice(0, 8)}…${text.slice(-6)}` : text || '—';
};

/** Purpose: Perform format kolkata date time for the custom data module. Why: Callers need a consistent result from this shared operation. */
const formatKolkataDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(date);
};

/** Purpose: Resolve csv escape for the custom data module. Why: The surrounding operation uses this value to make its decision. */
const csvEscape = (value) => {
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value ?? '');
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

/** Purpose: Perform parse csv line for the custom data module. Why: Callers need a consistent result from this shared operation. */
const parseCsvLine = (line = '') => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];

    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      index += 1;
      continue;
    }

    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }

    if (char === ',' && !inQuotes) {
      values.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current);
  return values.map((item) => item.trim());
};

/** Purpose: Perform parse import file for the custom data module. Why: Callers need a consistent result from this shared operation. */
const parseImportFile = async (file) => {
  const text = await file.text();
  if (file.name.toLowerCase().endsWith('.json')) {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed : [];
  }

  const rows = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (rows.length < 2) return [];
  const headers = parseCsvLine(rows[0]);
  return rows.slice(1).map((line) => {
    const values = parseCsvLine(line);
    return headers.reduce((accumulator, header, index) => {
      accumulator[header] = values[index] ?? '';
      return accumulator;
    }, {});
  });
};

/** Purpose: browse and manage tenant-defined data tables. Why: bot workflows need controlled access to custom records without leaving the workspace. */
export default function CustomData() {
  const { selectedBot, selectedEnv } = useAuth();
  const isProd = selectedEnv === 'prod';
  const importInputRef = useRef(null);
  const recordsRequestVersion = useRef(0);
  const [headerHost, setHeaderHost] = useState(() => (
    typeof document !== 'undefined' ? document.getElementById('custom-data-header-actions') : null
  ));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!headerHost && typeof document !== 'undefined') {
      const element = document.getElementById('custom-data-header-actions');
      if (element) setHeaderHost(element);
    }
  }, [headerHost]);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [recordSaving, setRecordSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [tables, setTables] = useState([]);
  const [records, setRecords] = useState([]);
  const [environmentId, setEnvironmentId] = useState('');
  const [selectedTableId, setSelectedTableId] = useState('');
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [tableForm, setTableForm] = useState({
    tableId: '',
    name: '',
    slug: '',
    description: '',
    fields: [emptyField()],
  });
  const [tableActionsAnchor, setTableActionsAnchor] = useState(null);
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [recordForm, setRecordForm] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [filterMatchMode, setFilterMatchMode] = useState('and');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [newFilterField, setNewFilterField] = useState('');
  const [newFilterOperator, setNewFilterOperator] = useState('contains');
  const [newFilterValue, setNewFilterValue] = useState('');
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [copiedValue, setCopiedValue] = useState('');
  const [viewingRecord, setViewingRecord] = useState(null);
  const [deletingRecordId, setDeletingRecordId] = useState(null);

  /** Purpose: Handle copy value in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const copyValue = async (value) => {
    const text = String(value ?? '');
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedValue(text);
      window.setTimeout(() => setCopiedValue((current) => (current === text ? '' : current)), 1500);
    } catch {
      // Clipboard fallback
    }
  };

  /** Purpose: Handle selected table in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const selectedTable = useMemo(
    () => tables.find((table) => table.tableId === selectedTableId) || null,
    [tables, selectedTableId]
  );

  /** Purpose: Transform available filter fields within the custom data module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const availableFilterFields = useMemo(() => {
    if (!selectedTable) return [];
    return [
      { key: 'recordId', label: 'Record ID', type: 'string' },
      ...(selectedTable.fields || []).map((f) => ({
        key: f.key,
        label: f.label || f.key,
        type: f.type || 'string',
      })),
      { key: 'updatedAt', label: 'Updated Date & Time', type: 'date' },
      { key: 'createdAt', label: 'Inserted Date & Time', type: 'date' },
    ];
  }, [selectedTable]);

  /** Purpose: Handle handle sort in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleSort = (columnKey) => {
    setPage(1);
    if (sortColumn === columnKey) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(columnKey);
      setSortDirection(columnKey === 'updatedAt' || columnKey === 'createdAt' ? 'desc' : 'asc');
    }
  };

  /** Purpose: Handle handle add filter in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleAddFilter = () => {
    const fieldKey = newFilterField || availableFilterFields[0]?.key;
    if (!fieldKey) return;
    /** Purpose: Resolve field obj for the custom data module. Why: The surrounding operation uses this value to make its decision. */
    const fieldObj = availableFilterFields.find((f) => f.key === fieldKey);
    const category = getCategoryForFieldType(fieldObj?.type);
    const isValLess = ['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(newFilterOperator);
    if (!isValLess && !String(newFilterValue).trim()) return;

    setActiveFilters((prev) => [
      ...prev,
      {
        id: `filter-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        fieldKey,
        fieldLabel: fieldObj?.label || fieldKey,
        fieldType: fieldObj?.type || 'string',
        category,
        operator: newFilterOperator,
        value: newFilterValue,
      },
    ]);
    setNewFilterValue('');
    setPage(1);
  };

  /** Purpose: Handle handle update filter in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleUpdateFilter = (filterId, updates) => {
    setActiveFilters((prev) =>
      prev.map((f) => {
        if (f.id !== filterId) return f;
        const next = { ...f, ...updates };
        if (updates.fieldKey && updates.fieldKey !== f.fieldKey) {
          /** Purpose: Resolve field obj for the custom data module. Why: The surrounding operation uses this value to make its decision. */
          const fieldObj = availableFilterFields.find((col) => col.key === updates.fieldKey);
          next.fieldType = fieldObj?.type || 'string';
          next.fieldLabel = fieldObj?.label || updates.fieldKey;
          next.category = getCategoryForFieldType(next.fieldType);
          const ops = OPERATORS_BY_CATEGORY[next.category] || OPERATORS_BY_CATEGORY.string;
          next.operator = ops[0]?.value || 'equals';
        }
        return next;
      })
    );
    setPage(1);
  };

  /** Purpose: Handle handle remove filter in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleRemoveFilter = (filterId) => {
    setActiveFilters((prev) => prev.filter((f) => f.id !== filterId));
    setPage(1);
  };

  /** Purpose: Handle handle clear all filters in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleClearAllFilters = () => {
    setActiveFilters([]);
    setSearchQuery('');
    setFilterMatchMode('and');
    setPage(1);
  };

  /** Purpose: Perform filtered records for the custom data module. Why: Callers need a consistent result from this shared operation. */
  const filteredRecords = useMemo(() => {
    let result = records;

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((record) => {
        if (String(record.recordId || '').toLowerCase().includes(q)) return true;
        if (!record.data) return false;
        return Object.entries(record.data).some(([key, val]) => {
          if (val === null || val === undefined) return false;
          const text = typeof val === 'object' ? JSON.stringify(val) : String(val);
          return text.toLowerCase().includes(q) || key.toLowerCase().includes(q);
        });
      });
    }

    if (activeFilters.length > 0) {
      result = result.filter((record) => {
        /** Purpose: Resolve eval rule for the custom data module. Why: The surrounding operation uses this value to make its decision. */
        const evalRule = (rule) => {
          const rawVal = getFieldValue(record, rule.fieldKey);
          return evaluateRule(rawVal, rule.operator, rule.value, rule.category);
        };
        return filterMatchMode === 'or' ? activeFilters.some(evalRule) : activeFilters.every(evalRule);
      });
    }

    if (sortColumn) {
      /** Purpose: Resolve field def for the custom data module. Why: The surrounding operation uses this value to make its decision. */
      const fieldDef = (selectedTable?.fields || []).find((f) => f.key === sortColumn);
      result = [...result].sort((a, b) => compareValues(a, b, sortColumn, sortDirection, fieldDef?.type));
    }

    return result;
  }, [records, searchQuery, activeFilters, filterMatchMode, sortColumn, sortDirection, selectedTable]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / pageSize));

  /** Purpose: Resolve paginated records for the custom data module. Why: The surrounding operation uses this value to make its decision. */
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  /** Purpose: Handle handle delete record in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleDeleteRecord = async (recordId) => {
    if (!selectedTable || !window.confirm('Are you sure you want to delete this record?')) return;
    setDeletingRecordId(recordId);
    try {
      await axios.delete(`/api/v1/custom-data/tables/${selectedTable.tableId}/records/${recordId}`);
      setRecords((current) => current.filter((r) => r.recordId !== recordId));
      setMessage({ type: 'success', text: 'Record deleted successfully.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete record.' });
    } finally {
      setDeletingRecordId(null);
    }
  };

  /** Purpose: Perform render cell content for the custom data module. Why: Callers need a consistent result from this shared operation. */
  const renderCellContent = (value, field) => {
    if (value === null || value === undefined || value === '') {
      return (
        <Typography component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
          —
        </Typography>
      );
    }

    if (typeof value === 'boolean') {
      return (
        <Chip
          size="small"
          label={value ? 'True' : 'False'}
          color={value ? 'success' : 'default'}
          variant="outlined"
          sx={{ fontWeight: 600, height: 22 }}
        />
      );
    }

    if (field?.key === 'channel' || (typeof value === 'string' && ['web', 'whatsapp'].includes(value.toLowerCase()))) {
      const isWa = String(value).toLowerCase() === 'whatsapp';
      return (
        <Chip
          size="small"
          label={isWa ? 'WhatsApp' : 'Web'}
          color={isWa ? 'success' : 'primary'}
          variant="filled"
          sx={{ fontWeight: 700, height: 22, textTransform: 'capitalize' }}
        />
      );
    }

    if (typeof value === 'object') {
      const preview = Array.isArray(value) ? `Array · ${value.length} items` : `Object · ${Object.keys(value).length} fields`;
      return (
        <Tooltip title="Open record details to inspect the full structured value">
          <Chip
            size="small"
            label={preview}
            variant="outlined"
            sx={{ fontFamily: 'monospace', fontSize: 11, height: 22 }}
          />
        </Tooltip>
      );
    }

    const strVal = String(value);

    // Monospace & copyable for IDs, UIDs, and Session IDs
    if (field?.key === 'sessionId' || field?.key === 'uid' || (strVal.length >= 20 && !strVal.includes(' '))) {
      return (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography
            component="span"
            title={strVal}
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              maxWidth: 180,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              bgcolor: 'action.hover',
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {strVal}
          </Typography>
          <Tooltip title={copiedValue === strVal ? 'Copied!' : 'Copy'}>
            <TableCopyButton
              label={`Copy ${field?.label || field?.key || 'value'}`}
              onClick={(e) => {
                e.stopPropagation();
                copyValue(strVal);
              }}
              sx={{ width: 22, height: 22, p: 0 }}
            >
              {copiedValue === strVal ? (
                <CheckIcon sx={{ fontSize: 13, color: 'success.main' }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              )}
            </TableCopyButton>
          </Tooltip>
        </Stack>
      );
    }

    return (
      <Typography
        component="span"
        sx={{
          fontSize: '0.84rem',
          maxWidth: 220,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'inline-block',
        }}
        title={strVal}
      >
        {strVal}
      </Typography>
    );
  };

  /** Purpose: Load fetch environment id for the custom data module. Why: The selected view needs current data to render or continue processing. */
  const fetchEnvironmentId = async () => {
    const response = await axios.get(`/api/v1/environments/${selectedBot}/${selectedEnv}`);
    return response.data?.environment?.envId || '';
  };

  /** Purpose: Load load tables for the custom data module. Why: The selected view needs current data to render or continue processing. */
  const loadTables = async (resolvedEnvironmentId) => {
    const response = await axios.get(`/api/v1/custom-data/tables?botId=${selectedBot}&environmentId=${resolvedEnvironmentId}`);
    const nextTables = Array.isArray(response.data?.tables) ? response.data.tables : [];
    setTables(nextTables);
    if (nextTables.length > 0) {
      setSelectedTableId((current) => current || nextTables[0].tableId);
    } else {
      setSelectedTableId('');
      setRecords([]);
    }
  };

  /** Purpose: Load load records for the custom data module. Why: The selected view needs current data to render or continue processing. */
  const loadRecords = async (tableId) => {
    const requestVersion = ++recordsRequestVersion.current;
    if (!tableId) {
      setRecords([]);
      return;
    }
    const response = await axios.get(`/api/v1/custom-data/tables/${tableId}/records`);
    if (requestVersion !== recordsRequestVersion.current) return;
    setRecords(Array.isArray(response.data?.records) ? response.data.records : []);
  };

  useEffect(() => {
    /** Purpose: Load bootstrap for the custom data module. Why: The selected view needs current data to render or continue processing. */
    const bootstrap = async () => {
      if (!selectedBot || !selectedEnv) {
        setLoading(false);
        setTables([]);
        setRecords([]);
        setMessage({ type: 'info', text: 'Select a bot and environment to manage custom data.' });
        return;
      }

      setLoading(true);
      try {
        const resolvedEnvironmentId = await fetchEnvironmentId();
        setEnvironmentId(resolvedEnvironmentId);
        await loadTables(resolvedEnvironmentId);
        setMessage(null);
      } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to load custom data tables.' });
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, [selectedBot, selectedEnv]);

  useEffect(() => {
    if (selectedTableId) {
      setPage(1);
      setSearchQuery('');
      setActiveFilters([]);
      setFilterMatchMode('and');
      setSortColumn('');
      setSortDirection('asc');
      setShowFilterPanel(false);
      const requestVersion = recordsRequestVersion.current + 1;
      loadRecords(selectedTableId).catch((error) => {
        if (requestVersion === recordsRequestVersion.current) {
          setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to load records.' });
        }
      });
    }
  }, [selectedTableId]);

  /** Purpose: Handle open create dialog in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const openCreateDialog = () => {
    if (isProd) {
      setMessage({ type: 'warning', text: 'PROD tables are read-only. Create and manage tables in DEV and release them through the Release Center.' });
      return;
    }
    setTableForm({
      tableId: '',
      name: '',
      slug: '',
      description: '',
      fields: [emptyField()],
    });
    setTableDialogOpen(true);
  };

  /** Purpose: Handle open edit dialog in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const openEditDialog = () => {
    if (isProd) {
      setMessage({ type: 'warning', text: 'PROD tables are read-only. Make schema changes in DEV and release them through the Release Center.' });
      return;
    }
    if (!selectedTable) return;
    setTableForm({
      tableId: selectedTable.tableId,
      name: selectedTable.name || '',
      slug: selectedTable.slug || '',
      description: selectedTable.description || '',
      fields: Array.isArray(selectedTable.fields) && selectedTable.fields.length > 0
        ? selectedTable.fields.map((field) => ({
            ...field,
            _rowId: createFieldRowId(),
            searchable: Boolean(field.searchable),
            defaultValue: field.defaultValue ?? '',
          }))
        : [emptyField()],
    });
    setTableDialogOpen(true);
  };

  /** Purpose: Handle save table in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const saveTable = async () => {
    if (isProd) {
      setMessage({ type: 'error', text: 'Editing tables in PROD is not allowed. Make schema changes in DEV and release them through the Release Center.' });
      return;
    }
    if (!selectedBot || !environmentId || !tableForm.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        botId: selectedBot,
        environmentId,
        name: tableForm.name,
        slug: tableForm.slug || normalizeKey(tableForm.name),
        description: tableForm.description,
        fields: tableForm.fields
          .map((field) => {
            const { _rowId, ...persistedField } = field;
            return {
              ...persistedField,
              key: normalizeKey(field.key || field.label),
              label: String(field.label || '').trim(),
              searchable: Boolean(field.searchable),
            };
          })
          .filter((field) => field.label && field.key),
      };

      if (tableForm.tableId) {
        await axios.put(`/api/v1/custom-data/tables/${tableForm.tableId}`, payload);
      } else {
        await axios.post('/api/v1/custom-data/tables', payload);
      }

      await loadTables(environmentId);
      setTableDialogOpen(false);
      setMessage({ type: 'success', text: 'Custom table saved successfully.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save custom table.' });
    } finally {
      setSaving(false);
    }
  };

  /** Purpose: Handle delete table in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const deleteTable = async () => {
    if (isProd) {
      setMessage({ type: 'warning', text: 'Dropping tables in PROD is not allowed. Make changes in DEV and release them through the Release Center.' });
      return;
    }
    if (!selectedTable || !window.confirm(`Delete table "${selectedTable.name}"?`)) return;
    try {
      await axios.delete(`/api/v1/custom-data/tables/${selectedTable.tableId}`);
      await loadTables(environmentId);
      setMessage({ type: 'success', text: 'Table deleted successfully.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete table.' });
    }
  };

  /** Purpose: Resolve truncate table for the custom data module. Why: The surrounding operation uses this value to make its decision. */
  const truncateTable = async () => {
    if (!selectedTable || !window.confirm(`Remove all records from "${selectedTable.name}"?`)) return;
    try {
      await axios.delete(`/api/v1/custom-data/tables/${selectedTable.tableId}/records`);
      await loadRecords(selectedTable.tableId);
      setMessage({ type: 'success', text: 'All records removed from the table.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to truncate table.' });
    }
  };

  /** Purpose: Handle download records in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const downloadRecords = () => {
    if (!selectedTable) return;
    const headers = ['recordId', ...(selectedTable.fields || []).map((field) => field.key), 'updatedAt', 'insertedAt'];
    const csv = [
      headers.join(','),
      ...records.map((record) => headers.map((header) => {
        if (header === 'recordId') return csvEscape(record.recordId);
        if (header === 'updatedAt') return csvEscape(formatKolkataDateTime(record.updatedAt || record.createdAt));
        if (header === 'insertedAt') return csvEscape(formatKolkataDateTime(record.createdAt));
        return csvEscape(record.data?.[header]);
      }).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedTable.slug || normalizeKey(selectedTable.name)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /** Purpose: Handle open record dialog in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const openRecordDialog = () => {
    if (!selectedTable) return;
    /** Purpose: Load initial form for the custom data module. Why: The selected view needs current data to render or continue processing. */
    const initialForm = (selectedTable.fields || []).reduce((accumulator, field) => {
      accumulator[field.key] = field.defaultValue ?? '';
      return accumulator;
    }, {});
    setRecordForm(initialForm);
    setRecordDialogOpen(true);
  };

  /** Purpose: Handle save record in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const saveRecord = async () => {
    if (!selectedTable) return;
    setRecordSaving(true);
    try {
      await axios.post(`/api/v1/custom-data/tables/${selectedTable.tableId}/records`, { data: recordForm });
      await loadRecords(selectedTable.tableId);
      setRecordDialogOpen(false);
      setMessage({ type: 'success', text: 'Record added successfully.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to add record.' });
    } finally {
      setRecordSaving(false);
    }
  };

  /** Purpose: Handle handle import records in the custom data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleImportRecords = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !selectedTable) return;

    setImporting(true);
    try {
      const importedRows = await parseImportFile(file);
      if (!importedRows.length) {
        setMessage({ type: 'warning', text: 'The selected file does not contain any records.' });
        return;
      }

      for (const row of importedRows) {
        /** Purpose: Resolve payload for the custom data module. Why: The surrounding operation uses this value to make its decision. */
        const payload = (selectedTable.fields || []).reduce((accumulator, field) => {
          if (Object.prototype.hasOwnProperty.call(row, field.key)) {
            accumulator[field.key] = row[field.key];
          } else if (Object.prototype.hasOwnProperty.call(row, field.label)) {
            accumulator[field.key] = row[field.label];
          }
          return accumulator;
        }, {});

        await axios.post(`/api/v1/custom-data/tables/${selectedTable.tableId}/records`, { data: payload });
      }

      await loadRecords(selectedTable.tableId);
      setMessage({ type: 'success', text: `Imported ${importedRows.length} records successfully.` });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || error.message || 'Failed to import records.' });
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <CustomTableLayout tableId={selectedTableId} config={CUSTOM_TABLE_LAYOUT_CONFIG}>
        <TableLoadingState rows={6} columns={4} />
      </CustomTableLayout>
    );
  }

  return (
    <CustomTableLayout tableId={selectedTableId} config={CUSTOM_TABLE_LAYOUT_CONFIG}>
      <Portal container={headerHost} disablePortal={!headerHost}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flexShrink: 0, pt: 0.75, pb: 0.25 }}>
          <TextField
            select
            size="small"
            label="Select table"
            value={selectedTableId}
            onChange={(event) => setSelectedTableId(event.target.value)}
            disabled={tables.length === 0}
            sx={{
              width: { xs: 130, sm: 155, md: 180 },
              minWidth: { xs: 120, sm: 140, md: 160 },
              maxWidth: { xs: 140, sm: 165, md: 190 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                height: 38,
                /** Purpose: Derive the background color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                fontSize: '0.8125rem',
              },
              '& .MuiSelect-select': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                pr: '28px !important',
                py: '8px !important',
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.8125rem',
                fontWeight: 600,
                lineHeight: 1.2,
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -6px) scale(0.75)',
                  /** Purpose: Derive the bgcolor style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? '#0f172a' : '#ffffff',
                  px: 0.5,
                  borderRadius: '4px',
                },
              },
            }}
          >
            {tables.map((table) => (
              <MenuItem key={table.tableId} value={table.tableId}>
                {table.name} ({table.fields?.length || 0} fields)
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant={selectedTable ? 'outlined' : 'contained'}
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={openCreateDialog}
            disabled={isProd}
            title={isProd ? 'PROD custom tables are read-only. Create tables in DEV.' : 'New table'}
            sx={{
              borderRadius: '10px',
              height: 38,
              textTransform: 'none',
              fontWeight: 650,
              fontSize: '0.8125rem',
              whiteSpace: 'nowrap',
              px: 1.25,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: isProd ? 'none' : '0 4px 12px rgba(37, 99, 235, 0.25)',
              },
            }}
          >
            New table
          </Button>

          {selectedTable && (
            <>
              <input
                ref={importInputRef}
                type="file"
                accept=".csv,.json"
                hidden
                onChange={handleImportRecords}
              />
              <Button
                variant="outlined"
                startIcon={<FileUploadIcon sx={{ fontSize: 16 }} />}
                onClick={() => importInputRef.current?.click()}
                disabled={importing}
                sx={{
                  borderRadius: '10px',
                  height: 38,
                  textTransform: 'none',
                  fontWeight: 650,
                  fontSize: '0.8125rem',
                  whiteSpace: 'nowrap',
                  px: 1.25,
                }}
              >
                {importing ? 'Importing...' : 'Import'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                onClick={downloadRecords}
                disabled={!records.length}
                sx={{
                  borderRadius: '10px',
                  height: 38,
                  textTransform: 'none',
                  fontWeight: 650,
                  fontSize: '0.8125rem',
                  whiteSpace: 'nowrap',
                  px: 1.25,
                }}
              >
                Export
              </Button>
              <Button
                variant="outlined"
                onClick={(event) => setTableActionsAnchor(event.currentTarget)}
                endIcon={<MoreHorizIcon sx={{ fontSize: 18 }} />}
                sx={{
                  borderRadius: '10px',
                  height: 38,
                  textTransform: 'none',
                  fontWeight: 650,
                  fontSize: '0.8125rem',
                  whiteSpace: 'nowrap',
                  px: 1.25,
                }}
              >
                Table actions
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={openRecordDialog}
                sx={{
                  borderRadius: '10px',
                  height: 38,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  whiteSpace: 'nowrap',
                  px: 1.5,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                  },
                }}
              >
                Add new record
              </Button>
            </>
          )}
        </Stack>
      </Portal>

      {isProd && (
        <Alert severity="info" sx={{ mb: 0.5 }}>
          PROD custom tables are read-only. Make schema changes in DEV and release them through the Release Center.
        </Alert>
      )}

      {message ? (
        <Alert severity={message.type} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      ) : null}

      {!selectedTable ? (
        <Paper sx={{ p: 4, borderRadius: 3, width: '100%', textAlign: 'center' }}>
          <Typography variant="body1" fontWeight={600} color="text.secondary" mb={tables.length === 0 ? 2 : 0}>
            {tables.length === 0
              ? 'No custom tables yet. Create a table to get started.'
              : 'Select a table from the dropdown above in the navigation bar to view its records.'}
          </Typography>
          {tables.length === 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, mt: 2 }}
            >
              Create your first table
            </Button>
          )}
        </Paper>
      ) : (
        <TableSurface sx={{ width: '100%' }}>
                {/* Records Toolbar */}
                <TableToolbar>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
                      Records
                    </Typography>
                    <TableRecordCount count={filteredRecords.length} />
                    {(searchQuery || activeFilters.length > 0) && (
                      <Typography variant="caption" color="text.secondary">
                        (filtered from {records.length} total)
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TableSearch
                      label="Search records"
                      placeholder="Search records..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPage(1);
                      }}
                      sx={{ width: { xs: '100%', sm: 220 } }}
                    />
                    <Button
                      size="small"
                      variant={activeFilters.length > 0 ? 'contained' : 'outlined'}
                      startIcon={<TuneIcon sx={{ fontSize: 17 }} />}
                      endIcon={
                        <KeyboardArrowDownIcon
                          sx={{
                            fontSize: 18,
                            transform: showFilterPanel ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s ease-in-out',
                          }}
                        />
                      }
                      onClick={() => setShowFilterPanel((prev) => !prev)}
                      sx={{
                        borderRadius: '10px',
                        height: 38,
                        textTransform: 'none',
                        fontWeight: 650,
                        fontSize: '0.8125rem',
                        whiteSpace: 'nowrap',
                        px: 2,
                        '& .MuiButton-startIcon': {
                          marginLeft: 0,
                          mr: 0.85,
                        },
                        '& .MuiButton-endIcon': {
                          marginRight: 0,
                          ml: 0.85,
                        },
                        ...(activeFilters.length === 0
                          ? {
                              /** Purpose: Derive the border color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.18)' : '#cbd5e1',
                              color: 'text.primary',
                              /** Purpose: Derive the background color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                              backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.5)' : '#ffffff',
                              '&:hover': {
                                borderColor: 'primary.main',
                                /** Purpose: Derive the background color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : '#f8fafc',
                              },
                            }
                          : {
                              boxShadow: 'none',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                              },
                            }),
                      }}
                    >
                      Filter
                      {activeFilters.length > 0 && (
                        <Box
                          component="span"
                          sx={{
                            ml: 0.85,
                            px: 0.75,
                            py: 0.1,
                            fontSize: '0.72rem',
                            fontWeight: 800,
                            borderRadius: '9999px',
                            bgcolor: 'primary.contrastText',
                            color: 'primary.main',
                            lineHeight: 1.4,
                            display: 'inline-flex',
                            alignItems: 'center',
                          }}
                        >
                          {activeFilters.length}
                        </Box>
                      )}
                    </Button>
                    <Tooltip title="Refresh records">
                      <IconButton
                        size="small"
                        onClick={() => selectedTable && loadRecords(selectedTable.tableId)}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', height: 38, width: 38 }}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableToolbar>

                {/* Multi-Condition Filter Panel */}
                <Collapse in={showFilterPanel}>
                  <Box
                    sx={{
                      p: { xs: 1.75, sm: 2.25 },
                      borderBottom: '1px solid',
                      /** Purpose: Derive the border color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                      borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                      /** Purpose: Derive the bgcolor style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.75)' : '#f8fafc',
                    }}
                  >
                    <Stack spacing={2}>
                      {/* Panel Header with Match Mode Toggle */}
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={1.5}
                        sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '8px',
                                /** Purpose: Derive the bgcolor style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(37, 99, 235, 0.2)' : '#dbeafe',
                                color: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <TuneIcon sx={{ fontSize: 16 }} />
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 750, fontSize: '0.875rem' }}>
                              Multiple Filter Conditions
                            </Typography>
                          </Stack>

                          <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={filterMatchMode}
                            onChange={(e, val) => {
                              if (val) {
                                setFilterMatchMode(val);
                                setPage(1);
                              }
                            }}
                            sx={{
                              height: 30,
                              borderRadius: '8px',
                              /** Purpose: Derive the bgcolor style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
                              border: '1px solid',
                              borderColor: 'divider',
                              p: '2px',
                              '& .MuiToggleButton-root': {
                                border: 'none',
                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 1.5,
                                py: 0,
                                fontSize: '0.75rem',
                                fontWeight: 650,
                                color: 'text.secondary',
                                '&.Mui-selected': {
                                  bgcolor: 'primary.main',
                                  color: '#ffffff',
                                  fontWeight: 750,
                                  '&:hover': { bgcolor: 'primary.dark' },
                                },
                              },
                            }}
                          >
                            <ToggleButton value="and">Match ALL (AND)</ToggleButton>
                            <ToggleButton value="or">Match ANY (OR)</ToggleButton>
                          </ToggleButtonGroup>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                          {activeFilters.length > 0 && (
                            <Button
                              size="small"
                              color="error"
                              onClick={handleClearAllFilters}
                              sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 650 }}
                            >
                              Clear All ({activeFilters.length})
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setShowFilterPanel(false)}
                            startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                            sx={{
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontSize: '0.8rem',
                              fontWeight: 650,
                              height: 30,
                            }}
                          >
                            Done
                          </Button>
                        </Stack>
                      </Stack>

                      {/* Active Conditions List (Editable) */}
                      {activeFilters.length > 0 && (
                        <Stack spacing={1}>
                          {activeFilters.map((rule, idx) => {
                            /** Purpose: Resolve field obj for the custom data module. Why: The surrounding operation uses this value to make its decision. */
                            const fieldObj = availableFilterFields.find((f) => f.key === rule.fieldKey);
                            const category = getCategoryForFieldType(fieldObj?.type);
                            const isValHidden = ['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(rule.operator);

                            return (
                              <Stack
                                key={rule.id}
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1}
                                alignItems={{ xs: 'stretch', md: 'center' }}
                                sx={{
                                  p: 1.25,
                                  borderRadius: '10px',
                                  /** Purpose: Derive the bgcolor style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#ffffff',
                                  border: '1px solid',
                                  /** Purpose: Derive the border color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0',
                                  /** Purpose: Derive the box shadow style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                  boxShadow: (theme) => theme.palette.mode === 'dark' ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.02)',
                                }}
                              >
                                <Chip
                                  label={idx === 0 ? 'Where' : filterMatchMode.toUpperCase()}
                                  size="small"
                                  color={idx === 0 ? 'default' : 'primary'}
                                  sx={{
                                    fontWeight: 800,
                                    minWidth: 64,
                                    textTransform: 'uppercase',
                                    fontSize: '0.72rem',
                                    height: 28,
                                    borderRadius: '6px',
                                  }}
                                />

                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                  <Select
                                    value={rule.fieldKey}
                                    onChange={(e) => handleUpdateFilter(rule.id, { fieldKey: e.target.value })}
                                    sx={{
                                      borderRadius: '8px',
                                      height: 36,
                                      fontSize: '0.8125rem',
                                      /** Purpose: Derive the background color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                      backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                                    }}
                                  >
                                    {availableFilterFields.map((f) => (
                                      <MenuItem key={f.key} value={f.key} sx={{ fontSize: '0.8125rem' }}>
                                        {f.label} ({f.type})
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                  <Select
                                    value={rule.operator}
                                    onChange={(e) => handleUpdateFilter(rule.id, { operator: e.target.value })}
                                    sx={{
                                      borderRadius: '8px',
                                      height: 36,
                                      fontSize: '0.8125rem',
                                      /** Purpose: Derive the background color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                      backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                                    }}
                                  >
                                    {(OPERATORS_BY_CATEGORY[category] || OPERATORS_BY_CATEGORY.string).map((op) => (
                                      <MenuItem key={op.value} value={op.value} sx={{ fontSize: '0.8125rem' }}>
                                        {op.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>

                                {!isValHidden ? (
                                  <TextField
                                    size="small"
                                    value={rule.value}
                                    placeholder="Enter filter value..."
                                    onChange={(e) => handleUpdateFilter(rule.id, { value: e.target.value })}
                                    sx={{
                                      flex: 1,
                                      minWidth: 160,
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        height: 36,
                                        fontSize: '0.8125rem',
                                        /** Purpose: Derive the background color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                                      },
                                    }}
                                  />
                                ) : (
                                  <Box
                                    sx={{
                                      flex: 1,
                                      minWidth: 160,
                                      height: 36,
                                      display: 'flex',
                                      alignItems: 'center',
                                      px: 1.5,
                                      color: 'text.secondary',
                                      fontSize: '0.78rem',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    (No value needed)
                                  </Box>
                                )}

                                <Tooltip title="Remove condition">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveFilter(rule.id)}
                                    sx={{
                                      borderRadius: '8px',
                                      width: 34,
                                      height: 34,
                                      '&:hover': {
                                        /** Purpose: Derive the bgcolor style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.08)',
                                      },
                                    }}
                                  >
                                    <DeleteIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            );
                          })}
                        </Stack>
                      )}

                      {/* Add Next Condition Row */}
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: '10px',
                          border: '1px dashed',
                          /** Purpose: Derive the border color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                          borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1',
                          /** Purpose: Derive the bgcolor style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.6)',
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'stretch', md: 'center' }}
                        >
                          <Chip
                            label={activeFilters.length === 0 ? 'Where' : `+ ${filterMatchMode.toUpperCase()}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 800,
                              minWidth: 64,
                              textTransform: 'uppercase',
                              fontSize: '0.72rem',
                              height: 28,
                              borderRadius: '6px',
                            }}
                          />

                          <FormControl size="small" sx={{ minWidth: 160 }}>
                            <Select
                              value={newFilterField || (availableFilterFields[0]?.key || '')}
                              onChange={(e) => {
                                const nextKey = e.target.value;
                                setNewFilterField(nextKey);
                                /** Purpose: Resolve field obj for the custom data module. Why: The surrounding operation uses this value to make its decision. */
                                const fieldObj = availableFilterFields.find((f) => f.key === nextKey);
                                const ops = OPERATORS_BY_CATEGORY[getCategoryForFieldType(fieldObj?.type)] || OPERATORS_BY_CATEGORY.string;
                                setNewFilterOperator(ops[0]?.value || 'contains');
                              }}
                              sx={{
                                borderRadius: '8px',
                                height: 36,
                                fontSize: '0.8125rem',
                                /** Purpose: Derive the background color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                              }}
                            >
                              {availableFilterFields.map((f) => (
                                <MenuItem key={f.key} value={f.key} sx={{ fontSize: '0.8125rem' }}>
                                  {f.label} ({f.type})
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select
                              value={newFilterOperator}
                              onChange={(e) => setNewFilterOperator(e.target.value)}
                              sx={{
                                borderRadius: '8px',
                                height: 36,
                                fontSize: '0.8125rem',
                                /** Purpose: Derive the background color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                              }}
                            >
                              {(OPERATORS_BY_CATEGORY[getCategoryForFieldType(availableFilterFields.find((f) => f.key === (newFilterField || availableFilterFields[0]?.key))?.type)] || OPERATORS_BY_CATEGORY.string).map((op) => (
                                <MenuItem key={op.value} value={op.value} sx={{ fontSize: '0.8125rem' }}>
                                  {op.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {!['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(newFilterOperator) ? (
                            <TextField
                              size="small"
                              placeholder="Enter filter value..."
                              value={newFilterValue}
                              onChange={(e) => setNewFilterValue(e.target.value)}
                              sx={{
                                flex: 1,
                                minWidth: 160,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                  height: 36,
                                  fontSize: '0.8125rem',
                                  /** Purpose: Derive the background color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                                },
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddFilter();
                                }
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 160,
                                height: 36,
                                display: 'flex',
                                alignItems: 'center',
                                px: 1.5,
                                color: 'text.secondary',
                                fontSize: '0.78rem',
                                fontStyle: 'italic',
                              }}
                            >
                              (No value needed)
                            </Box>
                          )}

                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                            onClick={handleAddFilter}
                            sx={{
                              textTransform: 'none',
                              px: 2,
                              height: 36,
                              borderRadius: '8px',
                              fontWeight: 650,
                              fontSize: '0.8125rem',
                              whiteSpace: 'nowrap',
                              boxShadow: 'none',
                            }}
                          >
                            Add Condition
                          </Button>
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                </Collapse>

                {/* Active Filter Chips Bar */}
                {(activeFilters.length > 0 || searchQuery) && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1,
                      px: 2,
                      py: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      /** Purpose: Derive the bgcolor style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#ffffff',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      Active filters:
                    </Typography>
                    {activeFilters.length > 1 && (
                      <Chip
                        size="small"
                        color="secondary"
                        variant="filled"
                        label={`Logic: ${filterMatchMode.toUpperCase()}`}
                        onClick={() => {
                          setFilterMatchMode((prev) => (prev === 'and' ? 'or' : 'and'));
                          setPage(1);
                        }}
                        sx={{ fontWeight: 800, cursor: 'pointer', height: 24, fontSize: '0.72rem' }}
                      />
                    )}
                    {searchQuery && (
                      <Chip
                        size="small"
                        label={`Search: "${searchQuery}"`}
                        onDelete={() => {
                          setSearchQuery('');
                          setPage(1);
                        }}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {activeFilters.map((rule, idx) => {
                      /** Purpose: Resolve op obj for the custom data module. Why: The surrounding operation uses this value to make its decision. */
                      const opObj = (OPERATORS_BY_CATEGORY[rule.category] || []).find((o) => o.value === rule.operator);
                      const opLabel = opObj?.label || rule.operator;
                      const isValHidden = ['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(rule.operator);
                      const prefix = activeFilters.length > 1 && idx > 0 ? `[${filterMatchMode.toUpperCase()}] ` : '';
                      const chipText = `${prefix}${rule.fieldLabel} ${opLabel}${isValHidden ? '' : ` "${rule.value}"`}`;
                      return (
                        <Chip
                          key={rule.id}
                          size="small"
                          label={chipText}
                          onDelete={() => handleRemoveFilter(rule.id)}
                          color="primary"
                          variant="outlined"
                        />
                      );
                    })}
                    <Button
                      size="small"
                      startIcon={<FilterAltOffIcon sx={{ fontSize: 15 }} />}
                      onClick={handleClearAllFilters}
                      sx={{ textTransform: 'none', fontSize: '0.8rem', py: 0.2 }}
                    >
                      Clear all
                    </Button>
                  </Box>
                )}

                {/* Table Container */}
                <TableContainer sx={{ maxHeight: 620, minHeight: 280, bgcolor: 'background.paper' }}>
                  <Table stickyHeader size="small" sx={{ minWidth: 700, borderCollapse: 'separate', borderSpacing: 0 }}>
                    <TableHeader>
                      <TableRow
                        sx={{
                          '& th': {
                            ...TABLE_HEADER_CELL_SX,
                            /** Purpose: Derive the bgcolor style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#f8fafc',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            color: 'text.secondary',
                            borderBottom: '1px solid',
                            borderRight: '1px solid',
                            /** Purpose: Derive the border color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                            borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                            py: 1.5,
                            whiteSpace: 'nowrap',
                          },
                          '& th:last-child': {
                            borderRight: 'none',
                          },
                        }}
                      >
                        <TableCell sx={{ width: 50, textAlign: 'center' }}>#</TableCell>
                        <TableCell sx={{ minWidth: 140 }} sortDirection={sortColumn === 'recordId' ? sortDirection : false}>
                          <TableSortLabel
                            active={sortColumn === 'recordId'}
                            direction={sortColumn === 'recordId' ? sortDirection : 'asc'}
                            onClick={() => handleSort('recordId')}
                          >
                            Record ID
                          </TableSortLabel>
                        </TableCell>
                        {(selectedTable.fields || []).map((field) => (
                          <TableCell key={field.key} sx={{ minWidth: 140 }} sortDirection={sortColumn === field.key ? sortDirection : false}>
                            <TableSortLabel
                              active={sortColumn === field.key}
                              direction={sortColumn === field.key ? sortDirection : 'asc'}
                              onClick={() => handleSort(field.key)}
                            >
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '0.02em' }}>
                                  {field.label || field.key}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', textTransform: 'lowercase' }}>
                                  {field.type}{field.searchable ? ' • search' : ''}
                                </Typography>
                              </Box>
                            </TableSortLabel>
                          </TableCell>
                        ))}
                        <TableCell sx={{ minWidth: 180 }} sortDirection={sortColumn === 'updatedAt' ? sortDirection : false}>
                          <TableSortLabel
                            active={sortColumn === 'updatedAt'}
                            direction={sortColumn === 'updatedAt' ? sortDirection : 'desc'}
                            onClick={() => handleSort('updatedAt')}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '0.02em' }}>
                                Updated Date & Time
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                                Kolkata (IST)
                              </Typography>
                            </Box>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }} sortDirection={sortColumn === 'createdAt' ? sortDirection : false}>
                          <TableSortLabel
                            active={sortColumn === 'createdAt'}
                            direction={sortColumn === 'createdAt' ? sortDirection : 'desc'}
                            onClick={() => handleSort('createdAt')}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '0.02em' }}>
                                Inserted Date & Time
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                                Kolkata (IST)
                              </Typography>
                            </Box>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 110 }}>Actions</TableCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {records.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={(selectedTable.fields?.length || 0) + 5} align="center" sx={{ py: 2 }}>
                            <TableEmptyState title="No records yet" description="Records created from bot flows or added manually will appear here.">
                              <TableChartOutlinedIcon sx={{ fontSize: 44, color: 'text.disabled' }} />
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={openRecordDialog}
                                sx={{ mt: 1 }}
                              >
                                Add First Record
                              </Button>
                            </TableEmptyState>
                          </TableCell>
                        </TableRow>
                      ) : paginatedRecords.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={(selectedTable.fields?.length || 0) + 5} align="center" sx={{ py: 6 }}>
                            <Typography variant="body2" color="text.secondary">
                              No records match {searchQuery || activeFilters.length > 0 ? 'the applied search or filters' : 'the criteria'}.
                            </Typography>
                            <Button size="small" onClick={handleClearAllFilters} sx={{ mt: 1 }}>
                              Clear Filters
                            </Button>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedRecords.map((record, index) => {
                          const rowNumber = (page - 1) * pageSize + index + 1;
                          return (
                            <TableRow
                              key={record.recordId}
                              hover
                              sx={{
                                '&:hover': { bgcolor: 'action.hover' },
                                '& td': {
                                  py: 1.25,
                                  fontSize: '0.84rem',
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  /** Purpose: Derive the border color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                                },
                                '& td:last-child': {
                                  borderRight: 'none',
                                },
                              }}
                            >
                              <TableCell sx={{ textAlign: 'center', color: 'text.secondary', fontWeight: 600 }}>
                                {rowNumber}
                              </TableCell>
                              <TableCell sx={{ minWidth: 140 }}>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontFamily: 'monospace',
                                      fontSize: '0.76rem',
                                      color: 'text.secondary',
                                      maxWidth: 100,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                    title={record.recordId}
                                  >
                                    {shortId(record.recordId)}
                                  </Typography>
                                  <Tooltip title={copiedValue === record.recordId ? 'Copied full ID' : 'Copy Record ID'}>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyValue(record.recordId);
                                      }}
                                      sx={{ width: 22, height: 22, p: 0 }}
                                    >
                                      {copiedValue === record.recordId ? (
                                        <CheckIcon sx={{ fontSize: 13, color: 'success.main' }} />
                                      ) : (
                                        <ContentCopyIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                              {(selectedTable.fields || []).map((field) => (
                                <TableCell key={`${record.recordId}-${field.key}`}>
                                  {renderCellContent(record.data?.[field.key], field)}
                                </TableCell>
                              ))}
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Typography
                                  component="span"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                    color: 'text.primary',
                                  }}
                                >
                                  {formatKolkataDateTime(record.updatedAt || record.createdAt)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Typography
                                  component="span"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                    color: 'text.primary',
                                  }}
                                >
                                  {formatKolkataDateTime(record.createdAt)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                <Tooltip title="View record details">
                                  <IconButton
                                    size="small"
                                    onClick={() => setViewingRecord(record)}
                                    sx={{ color: 'primary.main', mr: 0.5 }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete record">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={deletingRecordId === record.recordId}
                                    onClick={() => handleDeleteRecord(record.recordId)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredRecords.length > 0 && <TablePagination page={page} pageCount={totalPages} pageSize={pageSize} total={filteredRecords.length} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />}
              </TableSurface>
            )}

      <Dialog open={tableDialogOpen} onClose={() => !saving && setTableDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{tableForm.tableId ? 'Edit table' : 'Create new table'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <Typography variant="body2" color="text.secondary">
            Create the table name first, then define the fields you want to store. Technical keys are generated automatically in the background.
          </Typography>
          <TextField
            label="Table name"
            placeholder="customer_orders"
            value={tableForm.name}
            onChange={(event) => setTableForm((current) => ({
              ...current,
              name: event.target.value,
              slug: normalizeKey(event.target.value),
            }))}
            fullWidth
          />
          <TextField
            label="Description (optional)"
            value={tableForm.description}
            onChange={(event) => setTableForm((current) => ({ ...current, description: event.target.value }))}
            multiline
            minRows={2}
            fullWidth
          />

          <Typography variant="subtitle2" fontWeight={700}>Fields</Typography>
          <Stack spacing={1.5}>
            {tableForm.fields.map((field, index) => (
              <Paper key={field._rowId} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                  <TextField label="Field name" value={field.label} onChange={(event) => setTableForm((current) => ({
                    ...current,
                    fields: current.fields.map((item, currentIndex) => currentIndex === index
                      ? {
                          ...item,
                          label: event.target.value,
                          key: normalizeKey(event.target.value),
                        }
                      : item),
                  }))} fullWidth />
                  <TextField select label="Type" value={field.type} onChange={(event) => setTableForm((current) => ({
                    ...current,
                    fields: current.fields.map((item, currentIndex) => currentIndex === index ? { ...item, type: event.target.value } : item),
                  }))} sx={{ minWidth: 180 }}>
                    {FIELD_TYPES.map((type) => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
                  </TextField>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={Boolean(field.searchable)}
                        onChange={() => setTableForm((current) => ({
                          ...current,
                          fields: current.fields.map((item, currentIndex) => currentIndex === index ? { ...item, searchable: !item.searchable } : item),
                        }))}
                      />
                    )}
                    label="Searchable"
                    sx={{ minWidth: 140, m: 0 }}
                  />
                  <Stack spacing={0.5} direction={{ xs: 'row', md: 'column' }}>
                    <Button size="small" variant={field.required ? 'contained' : 'outlined'} onClick={() => setTableForm((current) => ({
                      ...current,
                      fields: current.fields.map((item, currentIndex) => currentIndex === index ? { ...item, required: !item.required } : item),
                    }))}>
                      Required
                    </Button>
                    <Button size="small" variant={field.unique ? 'contained' : 'outlined'} onClick={() => setTableForm((current) => ({
                      ...current,
                      fields: current.fields.map((item, currentIndex) => currentIndex === index ? { ...item, unique: !item.unique } : item),
                    }))}>
                      Unique
                    </Button>
                  </Stack>
                  <IconButton color="error" onClick={() => setTableForm((current) => ({
                    ...current,
                    fields: current.fields.filter((_, currentIndex) => currentIndex !== index),
                  }))}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Paper>
            ))}
          </Stack>

          <Button variant="text" startIcon={<AddIcon />} onClick={() => setTableForm((current) => ({ ...current, fields: [...current.fields, emptyField()] }))}>
            Add another field
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveTable}
            disabled={saving || !tableForm.name.trim() || isProd}
          >
            {saving ? 'Saving...' : isProd ? 'Read-only in PROD' : tableForm.tableId ? 'Update table' : 'Create table'}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={tableActionsAnchor}
        open={Boolean(tableActionsAnchor)}
        onClose={() => setTableActionsAnchor(null)}
      >
        <MenuItem
          disabled={isProd}
          onClick={() => {
            setTableActionsAnchor(null);
            if (isProd) {
              setMessage({ type: 'warning', text: 'PROD tables are read-only. Make schema changes in DEV and release them through the Release Center.' });
              return;
            }
            openEditDialog();
          }}
        >
          {isProd ? 'Edit table (Disabled in PROD)' : 'Edit table'}
        </MenuItem>
        <MenuItem onClick={() => {
          setTableActionsAnchor(null);
          truncateTable();
        }}>
          Truncate
        </MenuItem>
        <MenuItem onClick={() => {
          setTableActionsAnchor(null);
          downloadRecords();
        }}>
          Download
        </MenuItem>
        <MenuItem
          disabled={isProd}
          onClick={() => {
            setTableActionsAnchor(null);
            if (isProd) {
              setMessage({ type: 'warning', text: 'Dropping tables in PROD is not allowed. Make changes in DEV and release them through the Release Center.' });
              return;
            }
            deleteTable();
          }}
          sx={{ color: isProd ? 'text.disabled' : 'error.main' }}
        >
          {isProd ? 'Drop (Disabled in PROD)' : 'Drop'}
        </MenuItem>
      </Menu>

      <Dialog open={recordDialogOpen} onClose={() => !recordSaving && setRecordDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add new record</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {(selectedTable?.fields || []).map((field) => (
            field.type === 'boolean' ? (
              <TextField
                key={field.key}
                select
                label={field.label}
                value={String(recordForm[field.key] ?? '')}
                onChange={(event) => setRecordForm((current) => ({ ...current, [field.key]: event.target.value === 'true' }))}
                fullWidth
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="true">True</MenuItem>
                <MenuItem value="false">False</MenuItem>
              </TextField>
            ) : field.type === 'json' ? (
              <TextField
                key={field.key}
                label={field.label}
                value={typeof recordForm[field.key] === 'object' ? JSON.stringify(recordForm[field.key], null, 2) : String(recordForm[field.key] ?? '')}
                onChange={(event) => {
                  try {
                    setRecordForm((current) => ({ ...current, [field.key]: JSON.parse(event.target.value || '{}') }));
                  } catch (error) {
                    setRecordForm((current) => ({ ...current, [field.key]: event.target.value }));
                  }
                }}
                multiline
                minRows={4}
                fullWidth
              />
            ) : (
              <TextField
                key={field.key}
                label={field.label}
                type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                value={recordForm[field.key] ?? ''}
                onChange={(event) => setRecordForm((current) => ({ ...current, [field.key]: event.target.value }))}
                fullWidth
                InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
              />
            )
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordDialogOpen(false)} disabled={recordSaving}>Cancel</Button>
          <Button variant="contained" onClick={saveRecord} disabled={recordSaving}>
            {recordSaving ? 'Saving...' : 'Add record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Record Details Dialog */}
      <Dialog
        open={Boolean(viewingRecord)}
        onClose={() => setViewingRecord(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Record Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Table: {selectedTable?.name}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setViewingRecord(null)}>✕</IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '10px !important' }}>
          {viewingRecord && (
            <Stack spacing={2.5}>
              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      RECORD ID
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {viewingRecord.recordId}
                      </Typography>
                      <IconButton size="small" onClick={() => copyValue(viewingRecord.recordId)}>
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Stack>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      UPDATED DATE & TIME (IST)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {formatKolkataDateTime(viewingRecord.updatedAt || viewingRecord.createdAt)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      INSERTED DATE & TIME (IST)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {formatKolkataDateTime(viewingRecord.createdAt)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Typography variant="subtitle2" fontWeight={700}>
                Field Values
              </Typography>
              <TableContainer sx={{ border: '1px solid', /** Purpose: Derive the border color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */ borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        /** Purpose: Derive the bgcolor style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#f8fafc',
                        '& th': {
                          fontWeight: 700,
                          borderRight: '1px solid',
                          borderBottom: '1px solid',
                          /** Purpose: Derive the border color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                          borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                        },
                        '& th:last-child': {
                          borderRight: 'none',
                        },
                      }}
                    >
                      <TableCell sx={{ width: 200 }}>Field</TableCell>
                      <TableCell sx={{ width: 120 }}>Type</TableCell>
                      <TableCell>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selectedTable?.fields || []).map((field) => {
                      const val = viewingRecord.data?.[field.key];
                      return (
                        <TableRow
                          key={field.key}
                          hover
                          sx={{
                            '& td': {
                              borderRight: '1px solid',
                              borderBottom: '1px solid',
                              /** Purpose: Derive the border color style for the custom data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                            },
                            '& td:last-child': {
                              borderRight: 'none',
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600 }}>
                            {field.label || field.key}
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              {field.key}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={field.type} variant="outlined" sx={{ fontSize: 11 }} />
                          </TableCell>
                          <TableCell>
                            {typeof val === 'object' && val !== null ? (
                              <Box
                                component="pre"
                                sx={{
                                  m: 0,
                                  p: 1,
                                  bgcolor: 'grey.100',
                                  borderRadius: 1,
                                  fontSize: 12,
                                  overflowX: 'auto',
                                }}
                              >
                                {JSON.stringify(val, null, 2)}
                              </Box>
                            ) : val !== null && val !== undefined && val !== '' ? (
                              <Typography sx={{ fontFamily: ['sessionId', 'uid'].includes(field.key) ? 'monospace' : 'inherit', fontSize: 13 }}>
                                {String(val)}
                              </Typography>
                            ) : (
                              <Typography sx={{ color: 'text.disabled', fontStyle: 'italic' }}>—</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mb: 0.5, display: 'block' }}>
                  RAW JSON
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    bgcolor: '#1e293b',
                    color: '#f8fafc',
                    borderRadius: 2,
                    fontSize: 12,
                    overflowX: 'auto',
                  }}
                >
                  {JSON.stringify(viewingRecord.data || {}, null, 2)}
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingRecord(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </CustomTableLayout>
  );
}
